import { useCallback, useReducer, useMemo, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPasswordProtection } from '@/store/selectors/password-protection.selector';
import { setPasswordProtection } from '@/store/reducers/passwordProtectionSlice';
import { setPasswordProtectionUnlocked } from '@/store/reducers/appDataSlice';
import { extensionUtils, encodePassword } from '@/utils';
import { usePasswordReset } from '@/hooks/integrations/usePasswordReset.hook';
import { EXTENSION_MESSAGE_TYPE } from '@/models/extension.model';
import {
  BLOCK_TYPES,
  CHALLENGE_VALUE,
  DEFAULT_CHALLENGE_VALUE,
} from '@/pages/Blocked/BlockPage.model';
import { useBlockPageData } from '@/pages/Blocked/useBlockPageData.hook';
import { TranslationKey } from '@/types/i18n.types';
import { useEnterKeyHandler } from './useEnterKeyHandler.hook';

type InputType = 'password' | 'text';

export const UNLOCK_VIEW = {
  UNLOCK: 'unlock',
  FORGOT_PASSWORD: 'forgotPassword',
  FORGOT_PASSWORD_SUCCESS: 'forgotPasswordSuccess',
} as const;

export type UnlockView = (typeof UNLOCK_VIEW)[keyof typeof UNLOCK_VIEW];

export const UNLOCK_VARIANT = {
  BLOCK_PAGE: 'blockPage',
  DASHBOARD: 'dashboard',
} as const;

export type UnlockVariant =
  (typeof UNLOCK_VARIANT)[keyof typeof UNLOCK_VARIANT];

interface IUnlockTextKeys {
  readonly title: TranslationKey;
  readonly subtitle: TranslationKey;
  readonly errorText: TranslationKey;
  readonly placeholder: TranslationKey | '';
}

const DASHBOARD_TEXT_KEYS: IUnlockTextKeys = {
  title: 'passwordProtectionLock.title',
  subtitle: 'passwordProtectionLock.subtitle',
  errorText: 'passwordProtectionLock.wrongPassword',
  placeholder: 'passwordProtectionLock.enterPassword',
};

const PASSWORD_TEXT_KEYS: IUnlockTextKeys = {
  title: 'blockPage.unlock.title',
  subtitle: 'blockPage.unlock.subtitle',
  errorText: 'blockPage.unlock.passwordError',
  placeholder: 'blockPage.unlock.enterPassword',
};

const CHALLENGE_TEXT_KEYS: IUnlockTextKeys = {
  title: 'blockPage.unlock.secondStepTitle',
  subtitle: 'blockPage.unlock.secondStepSubtitle',
  errorText: 'blockPage.unlock.challengeError',
  placeholder: '',
};

const getUnlockTextKeys = (
  variant: UnlockVariant,
  showChallenge: boolean
): IUnlockTextKeys => {
  if (variant === UNLOCK_VARIANT.DASHBOARD) return DASHBOARD_TEXT_KEYS;
  return showChallenge ? CHALLENGE_TEXT_KEYS : PASSWORD_TEXT_KEYS;
};

const UNLOCK_ACTION = {
  SET_PASSWORD_INPUT: 'SET_PASSWORD_INPUT',
  SET_CHALLENGE_INPUT: 'SET_CHALLENGE_INPUT',
  SET_INPUT_ERROR: 'SET_INPUT_ERROR',
  SET_VIEW: 'SET_VIEW',
  SHOW_CHALLENGE: 'SHOW_CHALLENGE',
  HIDE_CHALLENGE: 'HIDE_CHALLENGE',
  RESET_CHALLENGE: 'RESET_CHALLENGE',
} as const;

interface UnlockState {
  readonly passwordInputValue: string;
  readonly challengeInputValue: string;
  readonly inputError: boolean;
  readonly currentView: UnlockView;
  readonly showChallenge: boolean;
}

type UnlockAction =
  | { type: typeof UNLOCK_ACTION.SET_PASSWORD_INPUT; payload: string }
  | { type: typeof UNLOCK_ACTION.SET_CHALLENGE_INPUT; payload: string }
  | { type: typeof UNLOCK_ACTION.SET_INPUT_ERROR; payload: boolean }
  | { type: typeof UNLOCK_ACTION.SET_VIEW; payload: UnlockView }
  | { type: typeof UNLOCK_ACTION.SHOW_CHALLENGE }
  | { type: typeof UNLOCK_ACTION.HIDE_CHALLENGE }
  | { type: typeof UNLOCK_ACTION.RESET_CHALLENGE };

const initialState: UnlockState = {
  passwordInputValue: '',
  challengeInputValue: '',
  inputError: false,
  currentView: UNLOCK_VIEW.UNLOCK,
  showChallenge: false,
};

const unlockReducer = (
  state: UnlockState,
  action: UnlockAction
): UnlockState => {
  switch (action.type) {
    case UNLOCK_ACTION.SET_PASSWORD_INPUT:
      return {
        ...state,
        passwordInputValue: action.payload,
        inputError: false,
      };
    case UNLOCK_ACTION.SET_CHALLENGE_INPUT:
      return {
        ...state,
        challengeInputValue: action.payload,
        inputError: false,
      };
    case UNLOCK_ACTION.SET_INPUT_ERROR:
      return { ...state, inputError: action.payload };
    case UNLOCK_ACTION.SET_VIEW:
      return { ...state, currentView: action.payload };
    case UNLOCK_ACTION.SHOW_CHALLENGE:
      return { ...state, showChallenge: true, inputError: false };
    case UNLOCK_ACTION.HIDE_CHALLENGE:
      return {
        ...state,
        showChallenge: false,
        inputError: false,
        challengeInputValue: '',
      };
    case UNLOCK_ACTION.RESET_CHALLENGE:
      return { ...state, showChallenge: false };
    default:
      return state;
  }
};

interface UsePasswordUnlockProps {
  readonly onClose?: () => void;
  readonly variant?: UnlockVariant;
}

interface UsePasswordUnlockReturn {
  // State
  readonly inputError: boolean;
  readonly currentView: UnlockView;
  readonly showChallenge: boolean;
  readonly email: string | null;

  // Computed values
  readonly challengeValue: string;
  readonly title: TranslationKey;
  readonly subtitle: TranslationKey;
  readonly errorText: TranslationKey;
  readonly inputValue: string;
  readonly inputType: InputType;
  readonly placeholder: TranslationKey | '';

  // Handlers
  readonly onInputValueChange: (value: string) => void;
  readonly onInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  readonly onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  readonly onCancelClick: () => void;
  readonly onUnlockClick: () => void;
  readonly onForgotPasswordClick: () => void;
  readonly onForgotPasswordApprove: () => Promise<void>;
  readonly onForgotPasswordCancel: () => void;
  readonly onForgotPasswordSuccessClose: () => void;
  readonly onDisableChallengeClick: () => Promise<void>;
}

export const usePasswordUnlock = ({
  onClose,
  variant = UNLOCK_VARIANT.BLOCK_PAGE,
}: UsePasswordUnlockProps): UsePasswordUnlockReturn => {
  const reduxDispatch = useDispatch();
  const blockPageData = useBlockPageData();
  const passwordProtectionState = useSelector(selectPasswordProtection);
  const resetPassword = usePasswordReset();

  const [state, dispatch] = useReducer(unlockReducer, initialState);

  const challengeValue = useMemo((): string => {
    const blockType = blockPageData.blockType ?? '';
    const challengeFunction = CHALLENGE_VALUE[blockType];

    if (typeof challengeFunction === 'function') {
      const value = blockPageData.website || blockPageData.word || '';
      return challengeFunction(value);
    }

    return (challengeFunction as string) ?? DEFAULT_CHALLENGE_VALUE;
  }, [blockPageData.blockType, blockPageData.website, blockPageData.word]);

  const isDashboard = variant === UNLOCK_VARIANT.DASHBOARD;

  const unlockPage = useCallback((): void => {
    if (isDashboard) {
      reduxDispatch(setPasswordProtectionUnlocked(true));
      return;
    }
    const messageType =
      blockPageData.blockType === BLOCK_TYPES.CATEGORY
        ? EXTENSION_MESSAGE_TYPE.RETURN_TO_URL_CATEGORY
        : EXTENSION_MESSAGE_TYPE.RETURN_TO_URL;
    extensionUtils.sendMessageToExtension(messageType).finally();
    onClose?.();
  }, [isDashboard, reduxDispatch, blockPageData.blockType, onClose]);

  const validatePassword = useCallback((): boolean => {
    const encodedInput = encodePassword(state.passwordInputValue);
    if (!encodedInput) {
      return false;
    }
    return encodedInput === passwordProtectionState.pass;
  }, [state.passwordInputValue, passwordProtectionState.pass]);

  const validateChallenge = useCallback((): boolean => {
    return state.challengeInputValue === challengeValue;
  }, [state.challengeInputValue, challengeValue]);

  const onInputValueChange = useCallback(
    (value: string): void => {
      const actionType = state.showChallenge
        ? UNLOCK_ACTION.SET_CHALLENGE_INPUT
        : UNLOCK_ACTION.SET_PASSWORD_INPUT;
      dispatch({ type: actionType, payload: value });
    },
    [state.showChallenge]
  );

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void =>
      onInputValueChange(e.target.value),
    [onInputValueChange]
  );

  const onCancelClick = useCallback((): void => {
    if (state.showChallenge) {
      dispatch({ type: UNLOCK_ACTION.HIDE_CHALLENGE });
    } else {
      onClose?.();
    }
  }, [state.showChallenge, onClose]);

  const onUnlockClick = useCallback((): void => {
    if (state.showChallenge) {
      if (!validateChallenge()) {
        dispatch({ type: UNLOCK_ACTION.SET_INPUT_ERROR, payload: true });
        return;
      }
      unlockPage();
      return;
    }

    if (!validatePassword()) {
      dispatch({ type: UNLOCK_ACTION.SET_INPUT_ERROR, payload: true });
      return;
    }

    if (!isDashboard && passwordProtectionState.addChallenge) {
      dispatch({ type: UNLOCK_ACTION.SHOW_CHALLENGE });
    } else {
      unlockPage();
    }
  }, [
    state.showChallenge,
    validateChallenge,
    validatePassword,
    isDashboard,
    passwordProtectionState.addChallenge,
    unlockPage,
  ]);

  const onKeyDown = useEnterKeyHandler(onUnlockClick);

  const onForgotPasswordClick = useCallback((): void => {
    dispatch({
      type: UNLOCK_ACTION.SET_VIEW,
      payload: UNLOCK_VIEW.FORGOT_PASSWORD,
    });
  }, []);

  const onForgotPasswordApprove = useCallback(async (): Promise<void> => {
    await resetPassword();
    dispatch({
      type: UNLOCK_ACTION.SET_VIEW,
      payload: UNLOCK_VIEW.FORGOT_PASSWORD_SUCCESS,
    });
  }, [resetPassword]);

  const onForgotPasswordCancel = useCallback((): void => {
    dispatch({ type: UNLOCK_ACTION.SET_VIEW, payload: UNLOCK_VIEW.UNLOCK });
  }, []);

  const onForgotPasswordSuccessClose = useCallback((): void => {
    onClose?.();
  }, [onClose]);

  const onDisableChallengeClick = useCallback(async (): Promise<void> => {
    extensionUtils
      .sendMessageToExtension(
        EXTENSION_MESSAGE_TYPE.TURN_OFF_PASSWORD_PROTECTION_CHALLENGE
      )
      .finally();

    // Update Redux state so the change persists across modal reopens
    reduxDispatch(setPasswordProtection({ addChallenge: false }));
    dispatch({ type: UNLOCK_ACTION.RESET_CHALLENGE });
  }, [reduxDispatch]);

  // Computed values
  const { title, subtitle, errorText, placeholder } = getUnlockTextKeys(
    variant,
    state.showChallenge
  );

  const inputValue = state.showChallenge
    ? state.challengeInputValue
    : state.passwordInputValue;

  const inputType: InputType = state.showChallenge ? 'text' : 'password';

  return {
    // State
    inputError: state.inputError,
    currentView: state.currentView,
    showChallenge: state.showChallenge,
    email: passwordProtectionState.email,

    // Computed values
    challengeValue,
    title,
    subtitle,
    errorText,
    inputValue,
    inputType,
    placeholder,

    // Handlers
    onInputValueChange,
    onInputChange,
    onKeyDown,
    onCancelClick,
    onUnlockClick,
    onForgotPasswordClick,
    onForgotPasswordApprove,
    onForgotPasswordCancel,
    onForgotPasswordSuccessClose,
    onDisableChallengeClick,
  };
};
